<script setup>
import { initializeMap } from '../hook/useMap';
import { useKmlLayer } from '../hook/useKMLData';
import { useGeolocationLayer } from '../hook/useMyGeolocation';
import { computed, reactive, ref } from 'vue';
import { MAX_TILT, useTiltController } from '../hook/useTiltController';
import { useMapListeners } from '../hook/useMapListeners';
import FullScreenModal from '../components/FullScreenModal.vue';
import SimpleModal from '../components/SimpleModal.vue';
import { usePointsOfInterestLayer } from '../hook/usePointsOfInterestLayer';
import { FOLLOW_ZOOM, LANGUAGES, MAIN_COLOR } from '../constants';
import { useLanguage } from '../hook/useLanguage';

const isModalOpen = ref(false);
const languageModalOpen = ref(false);
const leaveConfirmationModalOpen = ref(false);
const fullScreenModalState = reactive({ id:null, open: false });

const handleFullScreenModalClose = () => {
  fullScreenModalState.open = false;
  fullScreenModalState.id = null;
};

const handleFullScreenModalOpen = (id)=> {
    fullScreenModalState.open = true;
    fullScreenModalState.id = id;
}

const baseUrl = import.meta.env.BASE_URL || '/';
const { setLanguage, language } = useLanguage();
const { map: initialMap, mapError } = initializeMap();
const { map: mapKml, kmlError } = useKmlLayer(initialMap);
const { tilt } = useTiltController(mapKml);
const mapWithLayers = usePointsOfInterestLayer(mapKml, language, handleFullScreenModalOpen);
const { mapCenter } = useMapListeners(mapWithLayers);
const { map, coordinates, permissionStatus, geolocationError, updatePermissionStatus } = useGeolocationLayer({ map: mapWithLayers, mapCenter, tilt });

// a computed ref
const locationAvailable = computed(() => !!coordinates?.lat);

// Function to center the map smoothly
const centerMap = async () => {
    if (permissionStatus.value !== 'granted') {
        await updatePermissionStatus();
    }
    
    if (coordinates?.lat && map.value) {
        try {
            isModalOpen.value = false;
            map.value.moveCamera({
                center: new google.maps.LatLng(coordinates.lat, coordinates.lng),
                heading: 0,
                zoom: FOLLOW_ZOOM,
                tilt: tilt.value
            });
        } catch (error) {
            console.error('Error centering map:', error);
            isModalOpen.value = true;
        }
    } else {
        isModalOpen.value = true;
    }
};

// Function to handle logo click - show confirmation modal
const handleLogoClick = () => {
    leaveConfirmationModalOpen.value = true;
};

// Function to confirm leaving the app and redirect to jf-quintagrande website
const confirmLeave = () => {
    leaveConfirmationModalOpen.value = false;
    window.open('https://jf-quintagrande.pt', '_blank');
};
</script>

<template>
    <SimpleModal :isOpen="isModalOpen" @update:isOpen="(value) => isModalOpen = value">
        <template #title>
            <p class="text-h6">
                {{ $t("important") }}
            </p>
        </template>
        <template #default>
            <p> {{ $t("turnLocation") }}</p>
        </template>
    </SimpleModal>
    <SimpleModal :isOpen="languageModalOpen" @update:isOpen="(value) => languageModalOpen = value">
        <template #title>
            <p class="text-h6">
                {{ $t('chooseLanguage') }}
            </p>
        </template>
        <template #default>
            <div class="flex flex-col gap-2 w-full items-center justify-center">
                <v-btn @click="() => {
                    setLanguage(lang);
                    languageModalOpen = false;
                }" v-for="(lang, index) in LANGUAGES.filter((l) => l !== language)" :key="index">
                    <img width="40px" :src="`${baseUrl}images/flags/${lang}.png`" />
                </v-btn>
            </div>
        </template>
    </SimpleModal>
    <SimpleModal :isOpen="leaveConfirmationModalOpen" @update:isOpen="(value) => leaveConfirmationModalOpen = value">
        <template #title>
            <p class="text-h6">
                {{ $t('leaveApp') }}
            </p>
        </template>
        <template #default>
            <p>{{ $t('leaveAppConfirm') }}</p>
        </template>
        <template #footer>
            <div class="flex gap-2 justify-end">
                <v-btn @click="leaveConfirmationModalOpen = false" variant="outlined">
                    {{ $t('cancel') }}
                </v-btn>
                <v-btn @click="confirmLeave" :color="MAIN_COLOR">
                    {{ $t('yes') }}
                </v-btn>
            </div>
        </template>
    </SimpleModal>
    <div class="flex flex-col w-full h-screen">
        <div class="flex justify-between header gap-3 p-2">
            <img class="logo" :src="`${baseUrl}images/logo.png`" @click="handleLogoClick" style="cursor: pointer;">
            <p :style="{ color: MAIN_COLOR, fontSize: 'small' }" class="self-center">{{ $t('parish') }}</p>
            <v-btn class="w-full" @click="languageModalOpen = true" icon style="background-color: #101820">
                <v-icon>
                    <img :src="`${baseUrl}images/flags/${language}.png`" />
                </v-icon>
            </v-btn>
        </div>
        <div class=" w-full h-full relative">
            <div class="z-10 absolute top-0 right-0">
                <v-container class="flex flex-col">
                    <!-- Modal Component -->
                    <full-screen-modal v-if="fullScreenModalState.open"
                        :open="fullScreenModalState.open"
                        :id="fullScreenModalState.id"
                        @fullScreenModal:open="handleFullScreenModalOpen"
                        @fullScreenModal:close="handleFullScreenModalClose"
                    >
                    </full-screen-modal>
                </v-container>
            </div>

            <div class="z-10 absolute bottom-0 right-0">
                <v-container class="flex flex-col">
                    <div class="flex flex-col items-center gap-3">
                        <v-icon :color="'#101820'" :icon="'mdi-axis-x-rotate-clockwise'" />
                        <v-slider :color="MAIN_COLOR" v-model="tilt" min="0" :max="MAX_TILT" direction="vertical" />
                    </div>
                    <v-btn @click="centerMap()" icon style="background-color: #101820">
                        <v-icon :icon="locationAvailable ? 'mdi-crosshairs-gps' : 'mdi-crosshairs-question'"
                            :color="locationAvailable ? MAIN_COLOR : 'white'">
                        </v-icon>
                    </v-btn>
                </v-container>
            </div>
            <div id="map" class="w-full h-full"></div>
        </div>
    </div>
</template>

<style scoped>
.languageSelect {
    color: white;
}

.logo {
    width: 30px;
    object-fit: contain;
    cursor: pointer;
    transition: opacity 0.2s;
}

.logo:hover {
    opacity: 0.8;
}

:deep(.v-slider.v-input--vertical > .v-input__control) {
    min-height: 50px !important;
}

:deep(.v-slider.v-input--vertical) {
    margin-bottom: 0px;
}

.header {
    background-color: #101820;
}

.title {
    color: #a3e635;
}
</style>